// /admin/records (architecture sprint-05 §6.2, S8.1 + S8.2). Every filter, the sort and the page live
// in the URL, so the tracker can deep-link here. Changing a filter or the sort resets to page 1.
import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { Page } from '@/components/Page';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminRecords, useTeamExport } from '@/hooks/use-admin-records';
import { useMe } from '@/hooks/use-me';
import { useAdminProjects } from '@/hooks/use-projects';
import { useAdminUsers } from '@/hooks/use-users';
import { formatHours2 } from '@/lib/format';
import { defaultRecordsParams, nextSort, pageCount, parseRecordsParams, recordsSearch, type RecordsParams } from '@/lib/records-model';
import { useToday } from '@/lib/use-now';
import type { RecordSort } from '@/types/admin';
import { RecordsFilters } from './RecordsFilters';
import { RecordsPagination } from './RecordsPagination';
import { RecordsTable } from './RecordsTable';

type ExportFormat = 'csv' | 'xlsx';
const EXPORTS: Array<{ format: ExportFormat; label: string }> = [
  { format: 'csv', label: 'Export CSV' },
  { format: 'xlsx', label: 'Export XLSX' },
];

export function RecordsPage(): JSX.Element {
  const { data: me } = useMe();
  if (!me)
    return (
      <Page width="6xl">
        <Skeleton className="h-96 w-full" />
      </Page>
    );
  return <Records tz={me.timezone} />;
}

function Records({ tz }: { tz: string }): JSX.Element {
  const today = useToday(tz);
  const [search, setSearch] = useSearchParams();
  const users = useAdminUsers('all');
  const activeProjects = useAdminProjects('active');
  const archivedProjects = useAdminProjects('archived');

  const known = useMemo(
    () => ({
      userIds: users.data ? new Set(users.data.items.map((u) => u.id)) : undefined,
      projectIds:
        activeProjects.data && archivedProjects.data ? new Set([...activeProjects.data, ...archivedProjects.data].map((p) => p.id)) : undefined,
    }),
    [users.data, activeProjects.data, archivedProjects.data],
  );
  const { params, canonical } = parseRecordsParams(search, today, known);

  useEffect(() => {
    if (!canonical) setSearch(recordsSearch(params), { replace: true });
  }, [canonical, params, setSearch]);

  const records = useAdminRecords(params);
  const pages = records.data ? pageCount(records.data.total, params.size) : 1;

  // A page past the end (rows deleted, or a stale link): clamp to the last page.
  useEffect(() => {
    if (records.data && !records.isPlaceholderData && params.page > pages) setSearch(recordsSearch({ ...params, page: pages }), { replace: true });
  }, [records.data, records.isPlaceholderData, params, pages, setSearch]);

  const update = (next: Partial<RecordsParams>, resetPage = true): void => {
    setSearch(recordsSearch({ ...params, ...next, ...(resetPage ? { page: 1 } : {}) }));
  };

  const exporter = useTeamExport();
  const [inFlight, setInFlight] = useState<ExportFormat | null>(null);
  function runExport(format: ExportFormat): void {
    setInFlight(format);
    exporter.mutate({ params, format }, { onSettled: () => setInFlight(null) });
  }

  return (
    <Page width="6xl">
      <PageHeader
        title="Records"
        hint="Every team member's entries, read-only. Filter, sort and export what you see."
        actions={EXPORTS.map(({ format, label }) => (
          <Button key={format} variant="outline" size="sm" onClick={() => runExport(format)} disabled={inFlight === format}>
            <Download /> {inFlight === format ? 'Exporting…' : label}
          </Button>
        ))}
      />
      <RecordsFilters
        from={params.from}
        to={params.to}
        user={params.user}
        project={params.project}
        users={users.data?.items ?? []}
        activeProjects={activeProjects.data ?? []}
        archivedProjects={archivedProjects.data ?? []}
        onRangeChange={(r) => update(r)}
        onResetRange={() => {
          const d = defaultRecordsParams(today);
          update({ from: d.from, to: d.to });
        }}
        onUserChange={(user) => update({ user })}
        onProjectChange={(project) => update({ project })}
      />
      <p className="mb-3 text-sm text-muted-foreground" aria-live="polite">
        {records.data ? `${records.data.total} entries · ${formatHours2(records.data.totalSeconds)} h` : '…'}
      </p>
      {!records.data ? (
        <Skeleton className="h-72 w-full rounded-xl" />
      ) : records.data.items.length === 0 ? (
        <EmptyState title="No entries match these filters." />
      ) : (
        <RecordsTable rows={records.data.items} sort={params.sort} dir={params.dir} onSort={(column: RecordSort) => update(nextSort(params, column))} />
      )}
      <RecordsPagination
        page={params.page}
        pages={pages}
        size={params.size}
        onPage={(page) => update({ page }, false)}
        onSize={(size) => update({ size })}
      />
    </Page>
  );
}
